/**
 * Renders a text-first (text_v2) AI report — the same free-text "Day's/Month's
 * Insights" blob used by DaysInsightsReport/TextReport — into an existing jsPDF
 * document, starting at y=56. Emoji glyphs have no jsPDF font support, so each
 * one is rasterized via the browser's color emoji font (Canvas) and embedded
 * as an inline image, matching the on-screen report exactly.
 */
export function renderTextReportPdf(doc, text, { marginX = 48 } = {}) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - marginX * 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);

  const EMOJI_RE = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}]\u{FE0F}?/gu;
  const fontSize = 11;
  const emojiSize = fontSize * 1.05;
  const emojiImageCache = new Map();
  const getEmojiImage = (ch) => {
    if (emojiImageCache.has(ch)) return emojiImageCache.get(ch);
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const ctx = canvas.getContext('2d');
    ctx.font = '78px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(ch, 48, 52);
    const dataUrl = canvas.toDataURL('image/png');
    emojiImageCache.set(ch, dataUrl);
    return dataUrl;
  };

  const zeroWidth = doc.getTextWidth('0');
  const placeholder = '0'.repeat(Math.max(1, Math.round(emojiSize / zeroWidth)));
  const placeholderRe = new RegExp(placeholder, 'g');

  let ty = 56;
  text.split('\n').forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) { ty += 8; return; }

    const emojiQueue = [];
    const withPlaceholders = line.replace(EMOJI_RE, (m) => { emojiQueue.push(m); return placeholder; });

    const wrapped = doc.splitTextToSize(withPlaceholders, maxWidth);
    if (ty + wrapped.length * 15 > pageHeight - 48) { doc.addPage(); ty = 56; }

    wrapped.forEach((wrappedLine) => {
      let cursorX = marginX;
      let lastIndex = 0;
      placeholderRe.lastIndex = 0;
      let m;
      while ((m = placeholderRe.exec(wrappedLine)) !== null) {
        const textRun = wrappedLine.slice(lastIndex, m.index);
        if (textRun) {
          doc.text(textRun, cursorX, ty);
          cursorX += doc.getTextWidth(textRun);
        }
        const emojiChar = emojiQueue.shift();
        if (emojiChar) {
          try {
            doc.addImage(getEmojiImage(emojiChar), 'PNG', cursorX, ty - emojiSize * 0.82, emojiSize, emojiSize);
          } catch (e) { /* unsupported glyph — skip the image, keep layout intact */ }
        }
        cursorX += emojiSize + 1;
        lastIndex = m.index + m[0].length;
      }
      const rest = wrappedLine.slice(lastIndex);
      if (rest) doc.text(rest, cursorX, ty);
      ty += 15;
    });
    ty += 3;
  });
}

export default renderTextReportPdf;
